import { createContext, FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { Spinner } from 'softcat:components/spinner.tsx';
import { Locale } from 'softcat:config/locale.ts';
import { UserPreferencesConfig } from 'softcat:config/user-preferences.ts';
import { LocaleService } from 'softcat:services/locale.ts';

export type ContextDefaults = {
    contextDefaults?: {
        locale?: Locale;
        messages?: Record<string, string>;
    };
};

export type LocaleContextType = {
    locale: Locale;
    updateLanguage: (locale: Locale) => Promise<void>;
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const LocaleProvider: FunctionComponent<PropsWithChildren<ContextDefaults>> = ({ children, contextDefaults = {} }) => {
    const defaultLocale = (localStorage.getItem(UserPreferencesConfig.LanguageStorageKey) as Locale) || Locale.EN;
    const [locale, setLocale] = useState<Locale>(contextDefaults.locale || defaultLocale);
    const [messages, setMessages] = useState<Record<string, string> | undefined>(contextDefaults.messages);

    const updateLanguage = useCallback(async (locale: Locale) => {
        try {
            const messages = await LocaleService.getMessages(locale);

            setMessages(messages);
            setLocale(locale);
        } catch (error) {
            console.error(error);
        }
    }, []);

    const localeContextMemo = useMemo<LocaleContextType>(() => {
        return {
            locale,
            updateLanguage,
        };
    }, [locale, updateLanguage]);

    useEffect(() => {
        (async () => {
            if (!messages) {
                await updateLanguage(defaultLocale);
            }
        })();
    }, [defaultLocale, messages, updateLanguage]);

    if (messages === undefined) {
        return (
            <div className={'p-4'}>
                <Spinner />
            </div>
        );
    }

    return (
        <LocaleContext.Provider value={localeContextMemo}>
            <IntlProvider defaultLocale="en" locale={locale} messages={messages}>
                {children}
            </IntlProvider>
        </LocaleContext.Provider>
    );
};

export { LocaleContext, LocaleProvider };
